<template>
  <v-list-item
    :data-id="customAttribute.id"
    :key="customAttribute.id"
    style="border: 1px solid rgba(0, 0, 0, 0.12)"
    class="custom-attr-handle"
    two-line
  >
    <v-list-item-icon>
      <v-icon left>{{ attributeTypeIcon }}</v-icon>
    </v-list-item-icon>
    <v-list-item-content>
      <v-list-item-title>
        <v-tooltip :disabled="!isMobile" top>
          <template v-slot:activator="{ on, attrs }">
            <b v-on="on" v-bind="attrs">{{ customAttribute.name }}</b>
          </template>
          <span>{{ customAttribute.name }}</span>
        </v-tooltip>
      </v-list-item-title>
      <v-list-item-subtitle>
        <div style="display: flex; align-items: center">
          <div style="margin-right: 12px">
            Lesen:
            <Icon :tooltip="rAccessIcon.tooltip" color="default">{{
              rAccessIcon.icon
            }}</Icon>
          </div>
          <div>
            Schreiben:
            <Icon :tooltip="wAccessIcon.tooltip" color="default">{{
              wAccessIcon.icon
            }}</Icon>
          </div>
        </div>
      </v-list-item-subtitle>
    </v-list-item-content>

    <v-list-item-action>
      <div v-if="loading" style="display: flex">
        <div
          style="font-size: 12px; opacity: 0.6; margin-right: 8px"
          v-if="showLockedHint"
        >
          Update...
        </div>
        <v-progress-circular size="20" color="primary" indeterminate />
      </div>
      <PopupMenu v-else :items="menuItems" />
    </v-list-item-action>

    <CustomAttributesEditDialog
      v-model="editOpen"
      :custom-attribute="customAttribute"
      @updated="$emit('updated', $event)"
    />

    <CustomAttributeValueUpdater
      v-model="updaterOpen"
      :custom-attribute="customAttribute"
      @update-in-progress="emitUpdateInProgress"
    ></CustomAttributeValueUpdater>
  </v-list-item>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, watch } from "vue"
import CustomAttributesUpdateErrors from "app/modules/custom-attributes/CustomAttributesUpdateErrors.vue"
import CustomAttributeValueUpdater from "features/custom_attributes/CustomAttributeValueUpdater.vue"
import { CustomAttributeDefinition } from "suus-api"
import {
  mdiClose,
  mdiDelete,
  mdiDotsVertical,
  mdiDrag,
  mdiFileMove,
  mdiNoteText,
  mdiPen,
} from "@mdi/js"
import CustomAttributesEditDialog from "app/modules/custom-attributes/CustomAttributesEditDialog.vue"
import { useConfirmationService } from "features/common/confirmation-dialog/confirmation.service"
import { useIsMobile } from "features/common/vuetify/useVuetify"
import {
  CUSTOM_ATTRIBUTE_READ_ACCESS_ICONS,
  CUSTOM_ATTRIBUTE_TYPE_ICONS,
  CUSTOM_ATTRIBUTE_WRITE_ACCESS_ICONS,
} from "app/modules/custom-attributes/types"
import PopupMenu from "app/ui-primitives/menus/popup-menu/PopupMenu.vue"
import { useSnackbar } from "app/ui-primitives/snackbars/SnackbarProvider.vue"
import Icon from "app/ui-primitives/icons/Icon.vue"
import {
  CustomAttributesClient,
  LockedException,
} from "app/modules/custom-attributes/client"

export default defineComponent({
  name: "CustomAttributesListItem",
  components: {
    Icon,
    PopupMenu,
    CustomAttributesEditDialog,
    CustomAttributeValueUpdater,
    CustomAttributesUpdateErrors,
  },
  props: {
    customAttribute: {
      type: Object as PropType<CustomAttributeDefinition>,
    },
  },
  setup(props, { emit }) {
    const editOpen = ref(false)
    const openEdit = () => {
      editOpen.value = true
    }

    const updaterOpen = ref(false)
    const openUpdater = () => {
      updaterOpen.value = true
    }

    const confirmationService = useConfirmationService()
    const { showError, showSuccess } = useSnackbar()
    const deleteCustomAttribute = async () => {
      const confirmed = await confirmationService.open({
        title: "Möchtest du dieses Feld wirklich löschen?",
        text: "Wenn du dieses Feld löschst, kann es nicht wiederhergestellt werden.",
        confirmText: "Löschen",
      })

      if (confirmed) {
        try {
          await CustomAttributesClient.delete(props.customAttribute.id)
          emit("deleted", props.customAttribute.id)
          showSuccess("Das Feld wurde erfolgreich gelöscht.")
        } catch (e) {
          showError(
            "Das Feld konnte nicht gelöscht werden. Stell bitte sicher, dass keine Werte mehr dafür gespeichert sind und versuche es dann erneut."
          )
        }
      }
    }
    const menuActionLoading = ref(false)
    const confirmMove = async () =>
      props.customAttribute.attributable_type === "ClubRecord"
        ? await confirmationService.open({
            title: "Möchtest du dieses Feld wirklich verschieben?",
            text: "Wenn du das Feld von den Mitgliedschaftsdaten zur Personendaten verschiebst, werden nur die Daten der eigenen Verbindung übertragen. Die Daten der Mitgliedschaftsdaten werden gelöscht und können nicht wiederhergestellt werden.",
            confirmText: "Verschieben",
          })
        : true
    const moveCustomAttribute = async () => {
      if (!(await confirmMove())) return
      menuActionLoading.value = true
      try {
        const movedAttibute = await CustomAttributesClient.move(
          props.customAttribute.id,
          props.customAttribute.attributable_type === "PersonalRecord"
            ? "ClubRecord"
            : "PersonalRecord"
        )
        emit("moved", {
          movedAttribute: movedAttibute,
          targetList: movedAttibute.attributable_type,
        })
      } catch (error) {
        if (error instanceof LockedException) {
          showError(
            "Es ist bereits ein Update-Vorgang dieses Feldes im Gange, bitte probiere es später erneut."
          )
          emit("updated", {
            ...props.customAttribute,
            update_in_progress: true,
          })
          console.warn(error)
        }
        showError("Es gab einen Fehler beim Verschieben des Feldes.")
        console.error(error)
      } finally {
        menuActionLoading.value = false
      }
    }

    const isMobile = useIsMobile()

    const menuItems = [
      {
        key: "edit",
        title: "Bearbeiten",
        icon: mdiPen,
        clickHandler: openEdit,
      },
      {
        key: "update",
        title: "Werte-Import",
        icon: mdiNoteText,
        clickHandler: openUpdater,
      },
      {
        key: "delete",
        title: "Löschen",
        icon: mdiDelete,
        clickHandler: deleteCustomAttribute,
      },
      {
        key: "move",
        title: `Zu ${
          props.customAttribute.attributable_type === "PersonalRecord"
            ? "Mitgliedsdaten"
            : "Personendaten"
        } verschieben`,
        icon: mdiFileMove,
        clickHandler: moveCustomAttribute,
      },
    ]

    const attributeTypeIcon = computed(
      () => CUSTOM_ATTRIBUTE_TYPE_ICONS[props.customAttribute.attribute_type]
    )
    const rAccessIcon = computed(() => {
      return CUSTOM_ATTRIBUTE_READ_ACCESS_ICONS[
        props.customAttribute.read_access
      ]
    })
    const wAccessIcon = computed(() => {
      return CUSTOM_ATTRIBUTE_WRITE_ACCESS_ICONS[
        props.customAttribute.write_access
      ]
    })

    const loading = computed(() => {
      return props.customAttribute.update_in_progress || menuActionLoading.value
    })

    const showLockedHint = computed(() => {
      return props.customAttribute.update_in_progress
    })

    const emitUpdateInProgress = (value: boolean) => {
      emit("update-in-progress", value)
    }

    return {
      mdiDrag,
      mdiPen,
      mdiClose,
      mdiNoteText,
      mdiDelete,
      mdiDotsVertical,
      editOpen,
      updaterOpen,
      menuItems,
      isMobile,
      loading,
      attributeTypeIcon,
      rAccessIcon,
      wAccessIcon,
      showLockedHint,
      emitUpdateInProgress,
    }
  },
})
</script>

<style>
.custom-attr-handle {
  cursor: grab;
}

.custom-attr-handle:active {
  cursor: grabbing !important;
}

.chosen .custom-attr-handle {
  cursor: grabbing !important;
}

.sortable-ghost,
.sortable-chosen {
  cursor: grabbing !important;
}
</style>
